// API + utils para el módulo de entregas
import { supabase } from './supabase'

export const CUTOFF_HOUR = 16
export const DIAS = ['domingo','lunes','martes','miercoles','jueves','viernes','sabado']
export const DIA_LABEL = {
  lunes:'Lunes', martes:'Martes', miercoles:'Miércoles', jueves:'Jueves',
  viernes:'Viernes', sabado:'Sábado', domingo:'Domingo'
}

/** Antes del corte → reparte HOY. Después → MAÑANA. */
export function getDiaReparto(now = new Date()) {
  const hora = now.getHours()
  const dia = now.getDay()
  return hora < CUTOFF_HOUR ? dia : (dia + 1) % 7
}

export function getDiaRepartoLabel(now = new Date()) {
  return DIA_LABEL[DIAS[getDiaReparto(now)]]
}

export function isAfterCutoff(now = new Date()) {
  return now.getHours() >= CUTOFF_HOUR
}

/** Fecha que se va a repartir (today o tomorrow). */
export function getFechaReparto(now = new Date()) {
  const d = new Date(now)
  if (isAfterCutoff(now)) d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

/** Trae las entregas programadas para una fecha. */
export async function getEntregas(fecha) {
  const { data, error } = await supabase
    .from('entregas')
    .select(`
      id, numero_orden, tracking_token, fecha_programada, ventana_horaria, tipo,
      kit_codigo, productos_extras, estado, bultos_total,
      repartidor_id, ts_en_ruta, ts_entregada,
      ts_no_entregada, motivo_no_entrega, foto_url, recibido_por,
      cliente:clientes!entregas_cliente_id_fkey(
        id, barrio, direccion, apto, dia_entrega, alias,
        titular:profiles!clientes_titular_fkey(id, nombre, phone),
        beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone)
      ),
      kit:kits!entregas_kit_codigo_fkey(nombre, tier),
      repartidor:profiles!entregas_repartidor_id_fkey(nombre, role)
    `)
    .eq('fecha_programada', fecha)
    .order('ventana_horaria')
    .order('barrio', { foreignTable: 'cliente', ascending: true })
  if (error) {
    console.error('getEntregas error:', error)
    return []
  }
  return await mergearDatosPedidoOneShot(data || [])
}

/** Para entregas que vinieron de pedidos one-shot (sin cliente suscriptor),
 *  busca los datos del pedido y los inyecta como `cliente` mock para que la UI no se rompa. */
async function mergearDatosPedidoOneShot(entregas) {
  const sinCliente = entregas.filter(e => !e.cliente)
  if (sinCliente.length === 0) return entregas
  const ids = sinCliente.map(e => e.id)
  const { data: pedidos, error } = await supabase
    .from('pedidos')
    .select(`
      entrega_id, comprador_nombre, comprador_telefono, comprador_email,
      entrega_direccion, entrega_apto, entrega_lat, entrega_lng,
      entrega_destinatario_nombre, entrega_destinatario_telefono,
      monto_total, items, notas,
      barrio:barrios!pedidos_entrega_barrio_id_fkey(id, nombre)
    `)
    .in('entrega_id', ids)
  if (error) { console.error('mergearDatosPedidoOneShot:', error); return entregas }
  const byEntregaId = Object.fromEntries((pedidos || []).map(p => [p.entrega_id, p]))
  return entregas.map(e => {
    if (e.cliente) return e
    const pe = byEntregaId[e.id]
    if (!pe) return e
    const destinatario = pe.entrega_destinatario_nombre
    return {
      ...e,
      cliente: {
        alias: destinatario ? `📦 Para ${destinatario}` : '📦 Pedido único',
        barrio: pe.barrio?.nombre || '',
        direccion: pe.entrega_direccion || '',
        apto: pe.entrega_apto || '',
        lat: pe.entrega_lat,
        lng: pe.entrega_lng,
        notas: pe.notas,
        titular: {
          nombre: pe.comprador_nombre,
          phone: destinatario && pe.entrega_destinatario_telefono ? pe.entrega_destinatario_telefono : pe.comprador_telefono,
        },
        __esOneShot: true,
      },
    }
  })
}

/** Trae las entregas asignadas a un repartidor para una fecha. */
export async function getMisEntregas(repartidorId, fecha) {
  const { data, error } = await supabase
    .from('entregas')
    .select(`
      id, numero_orden, tracking_token, fecha_programada, ventana_horaria, tipo,
      kit_codigo, productos_extras, notas, estado, bultos_total,
      repartidor_id, ts_en_ruta, ts_entregada,
      ts_no_entregada, motivo_no_entrega, foto_url, recibido_por,
      cliente:clientes!entregas_cliente_id_fkey(
        id, barrio, direccion, apto, dia_entrega, tipo_vivienda, lat, lng, alias,
        titular:profiles!clientes_titular_fkey(id, nombre, phone),
        beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone)
      ),
      kit:kits!entregas_kit_codigo_fkey(nombre, tier)
    `)
    .eq('fecha_programada', fecha)
    .eq('repartidor_id', repartidorId)
    .order('ventana_horaria')
    .order('barrio', { foreignTable: 'cliente', ascending: true })
  if (error) {
    console.error('getMisEntregas error:', error)
    return []
  }
  return await mergearDatosPedidoOneShot(data || [])
}

/** Marca en ruta y dispara WA. */
export async function marcarEnRuta(entregaIds) {
  const { data, error } = await supabase.rpc('marcar_en_ruta', { entrega_ids: entregaIds })
  if (error) throw error
  // Dispara la edge function para procesar la cola
  await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-send`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` }
  }).catch(e => console.error('wa-send trigger:', e))
  return data
}

export async function confirmarEntrega({ token, nombre, doc, fotoPath }) {
  // foto_url en DB guarda el PATH del bucket privado (ej. "2026/05/abc.jpg"),
  // no la URL pública. Para mostrar usar getEntregaFotoSignedUrl(path).
  const { data, error } = await supabase.rpc('confirmar_entrega', {
    token_param: token,
    nombre_param: nombre,
    doc_param: doc,
    foto_url_param: fotoPath ?? null,
  })
  if (error) throw error
  // Dispara WA
  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-send`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` }
  }).catch(e => console.error('wa-send trigger:', e))
  return data
}

/** Marca una entrega como no entregada (visita fallida).
 *  motivo: string (chip predefinido + texto libre opcional concatenado).
 *  fotoPath: path en bucket entregas-fotos (obligatorio, prueba de la visita). */
export async function marcarNoEntregada({ token, motivo, fotoPath }) {
  const { data, error } = await supabase.rpc('marcar_no_entregada', {
    token_param: token,
    motivo_param: motivo,
    foto_url_param: fotoPath ?? null,
  })
  if (error) throw error
  // Dispara WA
  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wa-send`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` }
  }).catch(e => console.error('wa-send trigger:', e))
  return data
}

/** Sube una foto de entrega/visita al bucket privado y devuelve el PATH.
 *  kind: 'entrega' (entrega exitosa) o 'no-entrega' (visita fallida). */
export async function uploadEntregaFoto(token, file, kind = 'entrega') {
  if (!file) return null
  const yyyy = new Date().getFullYear()
  const mm = String(new Date().getMonth() + 1).padStart(2, '0')
  const prefix = kind === 'no-entrega' ? 'no-entregadas/' : ''
  const path = `${prefix}${yyyy}/${mm}/${token}.jpg`
  const { error } = await supabase.storage
    .from('entregas-fotos')
    .upload(path, file, { upsert: true, contentType: file.type })
  if (error) throw error
  return path
}

/** Genera signedUrl para una foto privada del bucket entregas-fotos.
 *  Default 7 dias (suficiente para la mayoria de reclamos). */
export async function getEntregaFotoSignedUrl(path, expiresIn = 7 * 24 * 3600) {
  if (!path) return null
  // Si por compatibilidad recibimos una URL completa vieja, la devolvemos como esta
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  const { data, error } = await supabase.storage
    .from('entregas-fotos')
    .createSignedUrl(path, expiresIn)
  if (error) { console.error('signedUrl:', error); return null }
  return data?.signedUrl || null
}

/** Trae entregas con composición de kit + extras expandidos para la hoja de armado. */
export async function getEntregasArmado(entregaIds) {
  if (!entregaIds?.length) return []
  // 1. Entregas + cliente + kit
  const { data: entregas, error } = await supabase
    .from('entregas')
    .select(`
      id, numero_orden, fecha_programada, ventana_horaria, tipo,
      kit_codigo, productos_extras, notas, bultos_total,
      cliente:clientes!entregas_cliente_id_fkey(
        barrio, direccion, apto, tipo_vivienda, alias,
        titular:profiles!clientes_titular_fkey(nombre),
        beneficiario:profiles!clientes_beneficiario_fkey(nombre)
      ),
      kit:kits!entregas_kit_codigo_fkey(nombre, tier)
    `)
    .in('id', entregaIds)
    .order('ventana_horaria')
  if (error) { console.error(error); return [] }
  // Mergear datos de pedidos one-shot (entregas sin cliente suscriptor)
  // OJO: no mutar el array original, re-asignar la variable
  const entregasMerged = await mergearDatosPedidoOneShot(entregas || [])

  // 2. Para cada kit_codigo único, traer composición
  const kitCodigos = [...new Set(entregasMerged.map(e => e.kit_codigo).filter(Boolean))]
  let kitItems = []
  if (kitCodigos.length > 0) {
    const { data } = await supabase
      .from('kit_items')
      .select(`
        kit_codigo, cantidad, es_regalo,
        producto:productos(id, sku, nombre, marca, presentacion)
      `)
      .in('kit_codigo', kitCodigos)
    kitItems = data || []
  }

  // 3. Mapear items por kit
  const itemsByKit = {}
  kitItems.forEach(it => {
    if (!itemsByKit[it.kit_codigo]) itemsByKit[it.kit_codigo] = []
    itemsByKit[it.kit_codigo].push(it)
  })

  // 4. Resolver productos_extras → traer nombres si vienen con producto_id o sku
  const extraIds = new Set()
  const extraSkus = new Set()
  entregasMerged.forEach(e => {
    (e.productos_extras || []).forEach(x => {
      if (x?.producto_id) extraIds.add(x.producto_id)
      else if (x?.sku) extraSkus.add(x.sku)
    })
  })
  let extrasMap = {}
  if (extraIds.size > 0 || extraSkus.size > 0) {
    const filters = []
    if (extraIds.size > 0) filters.push(`id.in.(${[...extraIds].join(',')})`)
    if (extraSkus.size > 0) filters.push(`sku.in.(${[...extraSkus].join(',')})`)
    const { data } = await supabase
      .from('productos')
      .select('id, sku, nombre, marca, presentacion')
      .or(filters.join(','))
    ;(data || []).forEach(p => { extrasMap[p.id] = p; extrasMap[p.sku] = p })
  }

  // 5. Construir resultado normalizado
  return entregasMerged.map(e => {
    const kitItemsResolved = (itemsByKit[e.kit_codigo] || []).map(it => ({
      cantidad: Number(it.cantidad),
      sku: it.producto?.sku,
      nombre: it.producto?.nombre,
      marca: it.producto?.marca,
      presentacion: it.producto?.presentacion,
      es_regalo: !!it.es_regalo,
    }))
    const extrasResolved = (e.productos_extras || []).map(x => {
      const ref = x?.producto_id ? extrasMap[x.producto_id] : (x?.sku ? extrasMap[x.sku] : null)
      return {
        cantidad: Number(x?.cantidad ?? x?.qty ?? 1),
        sku: ref?.sku || x?.sku,
        nombre: ref?.nombre || x?.nombre || (typeof x === 'string' ? x : 'Producto extra'),
        marca: ref?.marca,
        presentacion: ref?.presentacion,
        es_regalo: !!x?.es_regalo,
      }
    })
    return { ...e, kit_items: kitItemsResolved, extras: extrasResolved }
  })
}

/** Lista todas las suscripciones (clientes) con titular, beneficiario, kit, portero, barrio. */
export async function getSuscripciones() {
  const { data, error } = await supabase
    .from('clientes')
    .select(`
      id, alias, barrio, direccion, apto, dia_entrega, ventana_horaria, semana_mes,
      tipo_vivienda, lat, lng, estado, pausa_hasta, dia_cobro_mes, modelo_cobro,
      kit_actual, fecha_inicio, fecha_proximo_cobro, notas,
      fuera_zona, entrega_responsable,
      barrio_obj:barrios!clientes_barrio_id_fkey(id, nombre, dias),
      titular:profiles!clientes_titular_fkey(id, nombre, phone, email, documento, documento_tipo),
      beneficiario:profiles!clientes_beneficiario_fkey(id, nombre, phone, email, documento, documento_tipo),
      kit:kits!clientes_kit_actual_fkey(codigo, nombre, tier, precio_susc),
      portero:porteros!clientes_portero_referido_id_fkey(id, lugar, profile:profiles!porteros_id_fkey(nombre))
    `)
    .order('created_at', { ascending: false })
  if (error) { console.error('getSuscripciones:', error); return [] }
  return data || []
}

/** Lista de usuarios disponibles para repartir (admin + repartidor activos). */
export async function getUsuariosReparto() {
  const { data, error } = await supabase.rpc('usuarios_reparto')
  if (error) { console.error('getUsuariosReparto:', error); return [] }
  return data || []
}

/** Asigna un repartidor a una o varias entregas. Pasar null para desasignar. */
export async function asignarRepartidor(entregaIds, repartidorId) {
  const ids = Array.isArray(entregaIds) ? entregaIds : [entregaIds]
  const { error } = await supabase
    .from('entregas')
    .update({ repartidor_id: repartidorId || null })
    .in('id', ids)
  if (error) throw error
}

/** Reagenda una entrega no_entregada a una nueva fecha y la deja como programada.
 *  Limpia los datos de la visita fallida pero conserva foto_url para historico. */
export async function reagendarEntrega(entregaId, fechaNueva) {
  const { error } = await supabase
    .from('entregas')
    .update({
      estado: 'programada',
      fecha_programada: fechaNueva,
      ts_no_entregada: null,
      motivo_no_entrega: null,
      ts_en_ruta: null,
    })
    .eq('id', entregaId)
  if (error) throw error
}

export async function actualizarBultos(entregaId, bultos) {
  const n = Math.max(1, Math.min(20, parseInt(bultos) || 1))
  const { error } = await supabase
    .from('entregas')
    .update({ bultos_total: n })
    .eq('id', entregaId)
  if (error) throw error
  return n
}

export async function getTrackingPublico(token) {
  const { data, error } = await supabase.rpc('tracking_publico', { token_param: token })
  if (error) throw error
  return data?.[0] || null
}

/** Devuelve el "destinatario físico" — beneficiario si existe, sino titular. */
export function quienRecibe(cliente) {
  if (!cliente) return null
  return cliente.beneficiario || cliente.titular || null
}

/** Devuelve el "titular" — quien paga, recibe WA del cobro. */
export function quienPaga(cliente) {
  return cliente?.titular || null
}

/** True si beneficiario != titular (la suscripción es para otra persona). */
export function esRegalo(cliente) {
  return !!(cliente?.beneficiario && cliente?.beneficiario?.id !== cliente?.titular?.id)
}

export const ESTADO_LABEL = {
  programada: 'Programada',
  en_ruta: 'En ruta',
  entregada: 'Entregada',
  no_entregada: 'No entregada',
  cancelada: 'Cancelada',
}

export const ESTADO_COLOR = {
  programada: { bg: '#E0F2FE', text: '#0C4A6E', dot: '#0EA5E9' },
  en_ruta: { bg: '#FEF3C7', text: '#92400E', dot: '#F59E0B' },
  entregada: { bg: '#D1FAE5', text: '#065F46', dot: '#10B981' },
  no_entregada: { bg: '#FEE2E2', text: '#991B1B', dot: '#EF4444' },
  cancelada: { bg: '#F1F5F9', text: '#475569', dot: '#64748B' },
}
